import { Stack } from '@mui/material'
import { useAtomValue } from 'jotai'
import { lowerCase } from 'lodash'
import { memo } from 'react'
import { PerpPositionClearingsRecord } from '../../api/v1/perp'
import { PRIMARY_CURRENCY } from '../../constants/currencies'
import { useOrderValue, usePerpSize } from '../../hooks/useSizeCalculations'
import i18n from '../../i18n'
import { usePerpSymbols } from '../../store/perp/hooks'
import { OrderDirection } from '../../store/spot/types'
import { encryptionAddress } from '../../utils/address'
import { viewOnZKLinkExplorer } from '../../utils/explorer'
import { getEffectiveDecimals, toFixed } from '../../utils/number'
import { getOrderSide } from '../../utils/order'
import { findSymbolByName } from '../../utils/symbols'
import { orderModalAtom, useUpdateModalTitle } from './index'
import { OrderModalRow, OrderModalRowInfo, OrderModalRowLabel } from './styles'
import { DateFormatter } from '../../utils/datetime'

export const PerpClearingHistoryModal = memo(() => {
  const orderData = useAtomValue(orderModalAtom)
  const perpSymbols = usePerpSymbols()

  const data = orderData?.data as PerpPositionClearingsRecord
  useUpdateModalTitle(data?.symbol)

  const symbol = findSymbolByName(perpSymbols, data?.symbol)
  const {
    baseCurrency = '',
    quoteCurrency = '',
    quoteScale = 0,
    baseScale = 0,
    multiplier = 0,
  } = symbol ?? {}

  const {
    type = '',
    direction = OrderDirection.LONG,
    quantityChanged = 0,
    clearingPrice = 0,
    fee = 0,
    realizedPNLChanged = 0,
    txHash = '',
    orderId = 0,
    createdAt = 0,
  } = data
  const size = usePerpSize(Math.abs(quantityChanged), multiplier)
  const value = useOrderValue(size, clearingPrice)

  if (!data) {
    return null
  }

  let side = getOrderSide(direction)
  if (quantityChanged < 0) {
    side =
      direction === OrderDirection.LONG
        ? i18n.t('user-order-table-content-sell')
        : i18n.t('user-order-table-content-buy')
  }

  const fixedValue = toFixed(value, quoteScale)

  const fixedSize = toFixed(size, baseScale)
  const fixedPrice = toFixed(clearingPrice, quoteScale)

  const datetime = DateFormatter.toISODateTime(createdAt)

  return (
    <Stack>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Size</div>
          <div>Price</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={lowerCase(side)}>
            {fixedSize} {baseCurrency}
          </div>
          <div>
            {fixedPrice} {quoteCurrency}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Side</div>
          <div>Value</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={lowerCase(side)}>{side}</div>
          <div>
            {fixedValue} {quoteCurrency}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>

      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Type</div>
          <div>Fee</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>{type}</div>
          <div>
            {getEffectiveDecimals(fee)} {PRIMARY_CURRENCY}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Order ID</div>
          <div>Time</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>{orderId}</div>
          <div>{datetime}</div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Txid</div>
          <div>Pnl</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div onClick={() => viewOnZKLinkExplorer(txHash)}>
            {txHash ? encryptionAddress(txHash) : '-'}
          </div>
          <div className={realizedPNLChanged < 0 ? 'sell' : 'buy'}>
            {realizedPNLChanged} {PRIMARY_CURRENCY}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
    </Stack>
  )
})
